import { DeleteOutlined } from "@ant-design/icons";
import { Button, Flex, List, Modal, Select, Spin, Typography, message } from "antd";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "react-use";
import { useAppSelector } from "../../hooks/redux-hooks";
import { useFuseSearch } from "../../hooks/useFuseSearch";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { getClientShares, revokeClientShare, shareClient } from "../../service/client.service";
import { membersService } from "../../service/members.service";
import type { Member } from "../../service/members.service";
import type { TClientForm, TClientShare } from "../../types/index.type";
import styles from "./share-client-modal.module.scss";

interface ShareClientModalProps {
  visible: boolean;
  onClose: () => void;
  client: TClientForm;
  onShareSuccess?: () => void;
}

const ShareClientModal: React.FC<ShareClientModalProps> = ({ visible, onClose, client, onShareSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [groupMembers, setGroupMembers] = useState<Member[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [existingShares, setExistingShares] = useState<TClientShare[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [revokingUserId, setRevokingUserId] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [membersLoading, setMembersLoading] = useState(false);
  const [hasMoreMembers, setHasMoreMembers] = useState(false);
  const [totalMembers, setTotalMembers] = useState(0);
  const userData = useAppSelector((state) => state.user.data);
  const { referIndividualBy } = useSpecialityReference();
  const initialFetchDone = useRef(false);

  // Debounce search input for smoother UX
  useDebounce(
    () => {
      setDebouncedSearchValue(searchValue);
    },
    300,
    [searchValue],
  );

  // Fetch members from backend when search changes
  useEffect(() => {
    if (visible && initialFetchDone.current && debouncedSearchValue.trim()) {
      searchMembers(debouncedSearchValue);
    } else if (visible && initialFetchDone.current && !debouncedSearchValue.trim()) {
      // Reset to initial members when search is cleared
      fetchGroupMembers();
    }
  }, [debouncedSearchValue]);

  // Fetch group members and existing shares on modal open
  useEffect(() => {
    if (visible && client.id) {
      initialFetchDone.current = false;
      fetchGroupMembers();
      fetchExistingShares();
    }
  }, [visible, client.id]);

  const fetchGroupMembers = async () => {
    try {
      setMembersLoading(true);
      const response = await membersService.getMembers(1, undefined, 50);
      const members = response.results || [];
      setGroupMembers(members);
      setTotalMembers(response.count);
      setHasMoreMembers(response.count > members.length);
      initialFetchDone.current = true;
    } catch (error) {
      console.error("Error fetching group members:", error);
      message.error(`Sharing ${referIndividualBy.toLowerCase()}s is only available for group users`);
      initialFetchDone.current = false;
    } finally {
      setMembersLoading(false);
    }
  };

  const searchMembers = async (search: string) => {
    try {
      setMembersLoading(true);
      // Search backend with larger page size to get more matching results
      const response = await membersService.getMembers(1, search, 50);
      const members = response.results || [];
      setGroupMembers(members);
      setTotalMembers(response.count);
      setHasMoreMembers(response.count > members.length);
    } catch (error) {
      console.error("Error searching members:", error);
    } finally {
      setMembersLoading(false);
    }
  };

  const fetchExistingShares = async () => {
    if (!client.id) return;

    setLoadingShares(true);
    try {
      const response = await getClientShares(client.id);
      setExistingShares(response.data);
    } catch (error) {
      console.error("Error fetching existing shares:", error);
      // Don't show error message for 404 - it just means no shares exist yet
    } finally {
      setLoadingShares(false);
    }
  };

  const handleShare = async () => {
    if (!client.id || selectedUserIds.length === 0) {
      message.warning("Please select at least one member to share with");
      return;
    }

    setLoading(true);
    try {
      const response = await shareClient(client.id, selectedUserIds);

      if (response.data.created > 0) {
        message.success(`Successfully shared with ${response.data.created} member(s)`);
      }

      if (response.data.already_shared && response.data.already_shared.length > 0) {
        message.info(`${response.data.already_shared.length} member(s) already had access`);
      }

      if (response.data.invalid_users && response.data.invalid_users.length > 0) {
        message.warning(`${response.data.invalid_users.length} member(s) are not in your group`);
      }

      // Refresh the existing shares list
      await fetchExistingShares();

      // Clear selection
      setSelectedUserIds([]);

      // Call success callback
      if (onShareSuccess) {
        onShareSuccess();
      }
    } catch (error) {
      console.error("Error sharing client:", error);
      message.error("Failed to share client");
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (userId: number) => {
    if (!client.id) return;

    setRevokingUserId(userId);
    try {
      await revokeClientShare(client.id, userId);
      message.success("Access revoked successfully");

      // Refresh the existing shares list
      await fetchExistingShares();

      // Call success callback
      if (onShareSuccess) {
        onShareSuccess();
      }
    } catch (error) {
      console.error("Error revoking access:", error);
      message.error("Failed to revoke access");
    } finally {
      setRevokingUserId(null);
    }
  };

  const handleClose = () => {
    setSelectedUserIds([]);
    setSearchValue("");
    setDebouncedSearchValue("");
    onClose();
  };

  // Filter out members who already have access and the owner
  const existingShareUserIds = existingShares.map((share) => share.shared_with);
  const availableMembersBase = useMemo(
    () =>
      groupMembers.filter(
        (member) => member.id && !existingShareUserIds.includes(member.id) && member.id !== userData?.id,
      ),
    [groupMembers, existingShareUserIds, userData?.id],
  );

  // Use Fuse.js for additional fuzzy search across name and email (for email matching since backend only searches name)
  const { search: fuseSearch } = useFuseSearch({
    data: availableMembersBase,
    keys: ["name", "email"],
    threshold: 0.3,
  });

  // Apply fuzzy search filter on top of backend results for better email matching
  const availableMembers = useMemo(() => {
    if (!debouncedSearchValue.trim()) {
      return availableMembersBase;
    }
    // Use Fuse.js to further filter/sort results including email matching
    return fuseSearch(debouncedSearchValue);
  }, [availableMembersBase, debouncedSearchValue, fuseSearch]);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const isLoading = membersLoading;

  // Build the dropdown footer message
  const dropdownMessage = useMemo(() => {
    if (isLoading) return null;
    if (hasMoreMembers && !searchValue) {
      return `Showing ${groupMembers.length} of ${totalMembers} members. Type to search for more.`;
    }
    if (hasMoreMembers && searchValue) {
      return `Showing ${availableMembers.length} matching members. Refine your search for more specific results.`;
    }
    return null;
  }, [hasMoreMembers, searchValue, groupMembers.length, totalMembers, isLoading, availableMembers.length]);

  return (
    <Modal
      title={`Share "${client.name}"`}
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Flex vertical gap={20} className={styles.container}>
        {/* Share with new members */}
        <Flex vertical gap={10}>
          <Typography.Text strong>Share with members:</Typography.Text>
          <Select
            mode="multiple"
            placeholder="Search by name or email to select members"
            value={selectedUserIds}
            onChange={setSelectedUserIds}
            style={{ width: "100%" }}
            showSearch
            filterOption={false}
            onSearch={handleSearch}
            searchValue={searchValue}
            loading={isLoading}
            notFoundContent={isLoading ? <Spin size="small" /> : "No members found"}
            options={availableMembers.map((member) => ({
              label: `${member.name} (${member.email})`,
              value: member.id,
            }))}
            dropdownRender={(menu) => (
              <>
                {menu}
                {dropdownMessage && (
                  <Typography.Text
                    type="secondary"
                    style={{ display: "block", padding: "8px 12px", fontSize: "12px", borderTop: "1px solid #f0f0f0" }}
                  >
                    {dropdownMessage}
                  </Typography.Text>
                )}
              </>
            )}
          />
          {availableMembersBase.length === 0 && !isLoading && !searchValue && (
            <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
              All group members already have access to this {referIndividualBy.toLowerCase()}
            </Typography.Text>
          )}
          <Button type="primary" onClick={handleShare} loading={loading} disabled={selectedUserIds.length < 1}>
            Share
          </Button>
        </Flex>

        {/* List of current shares */}
        <Flex vertical gap={10}>
          <Typography.Text strong>Members with access ({existingShares.length}):</Typography.Text>
          {loadingShares ? (
            <Typography.Text type="secondary">Loading...</Typography.Text>
          ) : existingShares.length > 0 ? (
            <List
              className={styles.sharesList}
              dataSource={existingShares}
              renderItem={(share) => (
                <List.Item
                  actions={[
                    <Button
                      key="revoke"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRevoke(share.shared_with)}
                      loading={revokingUserId === share.shared_with}
                    >
                      Revoke
                    </Button>,
                  ]}
                >
                  <List.Item.Meta title={share.shared_with_name} description={share.shared_with_email} />
                </List.Item>
              )}
            />
          ) : (
            <Typography.Text type="secondary">This client has not been shared with anyone yet</Typography.Text>
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ShareClientModal;
